<script lang="ts">
interface Props {
    modelValue: string[]
    label?: string
    required?: boolean
}
</script>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import MyErrorMessage from '@/components/my-components/form/MyErrorMessage.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'

const { t } = useI18n()

const props = defineProps<Props>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: string[]): void
}>()

const errorMessage = ref('')

const mails = ref(props.modelValue.length ? props.modelValue : [''])

function validate(mail: string) {
    if (mail?.trim() === '') return false

    return true
}

function removeMail(index: number) {
    if (props.modelValue.length == 1) {
        errorMessage.value = t('emailRequired')
        return
    }
    errorMessage.value = ''
    mails.value.splice(index, 1)

    emit('update:modelValue', mails.value)
}

function handleKeyUp(index: number, e: KeyboardEvent) {
    emit('update:modelValue', mails.value)
}

function handlePaste(e: ClipboardEvent, index: number) {
    e.preventDefault()

    const pastedText = e.clipboardData?.getData('text/plain') || ''
    const emails = pastedText
        .split(/[ ,]+/)
        .map((email) => email.trim())
        .filter((email) => email.length > 0)

    // Replace current field with first email and append the rest
    mails.value[index] = emails[0]
    const remainingEmails = emails.slice(1).filter((email) => !mails.value.includes(email))
    mails.value.push(...remainingEmails)

    emit('update:modelValue', mails.value)
}

function handleNewMail() {
    if (!validate(mails.value[mails.value.length - 1])) {
        errorMessage.value = t('invalidEmail')
        return
    }

    mails.value.push('')
    errorMessage.value = ''
}

watch(
    () => props.modelValue,
    () => {
        if (props.modelValue !== mails.value) {
            mails.value = props.modelValue
        }
    },
)
</script>

<template>
    <div class="flex flex-col w-full space-y-2">
        <div v-for="(_, index) in mails" :key="index" class="flex items-center space-x-3">
            <MyInput
                v-model="mails[index]"
                type="email"
                class="w-full"
                group-class="grow"
                autocomplete="email"
                :name="`emails.${index}`"
                :label="index === 0 ? (props.label ?? t('email')) : undefined"
                :placeholder="t('email')"
                :required="required || index > 0"
                @keyup="handleKeyUp(index, $event)"
                @paste="handlePaste($event, index)"
            />

            <MyButton
                v-if="index > 0"
                v-tooltip="t('removeEmail')"
                type="button"
                class="dark:text-primary-50"
                size="small"
                pill
                icon
                @click.prevent="removeMail(index)"
            >
                <mdi:trash-can class="h-5 w-5" />
            </MyButton>
        </div>

        <MyErrorMessage :error="errorMessage" />

        <MyButton
            plain
            scheme="primary"
            type="button"
            class="self-end"
            size="small"
            @click="handleNewMail"
        >
            <mdi:plus />
            {{ t('addEmail') }}
        </MyButton>
    </div>
</template>
